import type { NodeRendererProps } from 'react-arborist'
import type { TreeData } from './WikiTree'
import { router } from '@inertiajs/react'
import { ChevronRight, FileText, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { ContextMenu, ContextMenuTrigger, ContextMenuContent, ContextMenuItem } from '@/components/ui/context-menu'

export default function WikiTreeNode({ node, style, dragHandle }: NodeRendererProps<TreeData>) {
  const [hovered, setHovered] = useState(false)
  const isSelected = node.isSelected
  const isEditing = node.isEditing

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!isEditing) {
      if (node.data.children.length > 0 && !node.isOpen) {
        node.toggle()
      }
      router.visit(`/notes/${node.data.slug}`)
    }
  }

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    node.toggle()
  }

  const handleAddChild = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    e.nativeEvent.stopImmediatePropagation()
    router.post(`/notes/${node.data.slug}/children`, {
      note: { title: 'Untitled' },
    })
  }

  const handleDelete = () => {
    router.delete(`/notes/${node.data.slug}`, { preserveState: false })
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
    <div
      ref={dragHandle}
      style={style}
      className={`flex items-center gap-0.5 rounded-md px-1 py-0.5 text-xs cursor-pointer select-none group ${
        isSelected
          ? 'bg-gray-200 text-gray-900'
          : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
      }`}
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDoubleClick={(e) => {
        e.stopPropagation()
        node.edit()
      }}
    >
      {/* Expand/collapse arrow */}
      <button
        onClick={handleToggle}
        className="flex-shrink-0 w-4 h-4 flex items-center justify-center rounded hover:bg-gray-200"
      >
        {node.data.children.length > 0 ? (
          <ChevronRight
            className={`h-3 w-3 transition-transform ${node.isOpen ? 'rotate-90' : ''}`}
          />
        ) : (
          <span className="w-3" />
        )}
      </button>

      {/* Icon */}
      <FileText className="h-3 w-3 flex-shrink-0 text-gray-400" />

      {/* Title or edit input */}
      {isEditing ? (
        <input
          autoFocus
          defaultValue={node.data.name}
          onBlur={() => node.reset()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') node.submit(e.currentTarget.value)
            if (e.key === 'Escape') node.reset()
          }}
          onClick={(e) => e.stopPropagation()}
          className="flex-1 bg-white border border-gray-300 rounded px-1 py-0 text-xs text-gray-900 outline-none min-w-0"
        />
      ) : (
        <span className="truncate flex-1 min-w-0">{node.data.name}</span>
      )}

      {/* Add child button on hover */}
      {hovered && !isEditing && (
        <button
          onClick={handleAddChild}
          onPointerDown={(e) => { e.stopPropagation(); e.preventDefault() }}
          className="flex-shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
          title="Add child page"
        >
          <Plus className="h-3 w-3" />
        </button>
      )}
    </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem variant="destructive" onClick={handleDelete}>
          <Trash2 className="h-3.5 w-3.5" />
          Delete page
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  )
}
